/**
 * StockMovementPanel — volet unique de mouvement de stock, mode `sortie` ou `entree`.
 * Réutilisé par la fiche vin (dans un tiroir glissant, asDrawer) ET par l'écran Sortie (en page).
 * Charge la cuvée pour proposer les pastilles millésime × format (changement de référence sans fermer),
 * met à jour l'état « Stock N → M » en direct, applique le prix selon le motif (sortie) ou pré-remplit
 * prix d'achat/fournisseur/emplacement (entrée). Toast « Annuler » 10 s. Aucune ressaisie.
 */
import { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { caveApi, scannerApi, metaApi } from '../../services/api';
import { sortieExit, sortieUndo } from '../../offline/store';
import { usePublicMode } from '../../context/PublicModeContext';
import { useUndoToast } from '../../hooks/useUndoToast';
import { formatEuro } from '../../utils/format';
import { Button } from '../../components/ui/kit';
import { QuantityStepper } from '../../components/ui/QuantityStepper';
import { MotifPicker } from '../../components/wine/MotifPicker';
import { WineColorDot, MaskedValue } from '../../components/wine/wine';
import { QUANTITY_SHORTCUTS } from '../../constants';
import { invalidateStock } from '../../services/refresh';

// Prix unitaire selon le motif (miroir de sortie_service.exit_unit_price).
function unitPrice(ref, motif, qty) {
  if (!ref) return null;
  if (motif === 'table') return ref.price_table;
  if (motif === 'emporter') {
    if (ref.price_table == null) return ref.price_emporter ?? null;
    return ref.price_table - (qty >= 3 ? 8 : 6);
  }
  if (motif === 'verre') return ref.price_verre;
  if (motif === 'offert_perso') return ref.purchase_price_ht;
  return null; // dégustation, casse
}

// Formats standards proposables en entrée (pour « + Nouveau »).
const STD_FORMATS = [
  { key: '375', label: '37,5 cl' }, { key: '750', label: '75 cl' },
  { key: 'magnum', label: 'Magnum' }, { key: 'jeroboam', label: 'Jéroboam' },
];

export default function StockMovementPanel({ cuveeId, initialRefId, mode = 'sortie', asDrawer = false, onClose, onDone }) {
  const qc = useQueryClient();
  const { isPublic } = usePublicMode();
  const { notify } = useUndoToast();

  const { data } = useQuery({ queryKey: ['cuvee', cuveeId], queryFn: () => caveApi.cuvee(cuveeId), enabled: !!cuveeId });
  const references = data?.references || [];
  const cuvee = data?.cuvee;
  const domain = data?.domain;

  const [refId, setRefId] = useState(initialRefId || null);
  const [quantity, setQuantity] = useState(1);
  const [motif, setMotif] = useState('table');
  const [note, setNote] = useState('');
  const [extraFormat, setExtraFormat] = useState(null); // format ajouté en entrée (qty 0)
  const [purchase, setPurchase] = useState('');
  const [supplier, setSupplier] = useState('');
  const [byGlass, setByGlass] = useState(false);
  const [onList, setOnList] = useState(true);

  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: metaApi.settings, enabled: mode === 'entree' });
  useEffect(() => {
    if (mode === 'entree' && settings) {
      setByGlass(!!settings.default_by_glass);
      setOnList(settings.default_on_wine_list !== false);
    }
  }, [mode, settings]);

  // Sélectionne une référence par défaut dès que la cuvée est chargée.
  useEffect(() => {
    if (!refId && references.length) setRefId(initialRefId || references.find((r) => r.quantity > 0)?.id || references[0].id);
  }, [references, initialRefId, refId]);

  const years = useMemo(() => [...new Set(references.map((r) => r.year))].sort((a, b) => String(b).localeCompare(String(a))), [references]);
  const selectedRef = references.find((r) => r.id === refId) || null;
  const activeYear = selectedRef?.year || years[0];

  // Formats disponibles pour le millésime actif (+ format ajouté en entrée).
  const formatsForYear = useMemo(() => {
    const present = references.filter((r) => r.year === activeYear);
    const list = present.map((r) => ({ key: r.format, label: r.format_label, qty: r.quantity, refId: r.id }));
    if (mode === 'entree' && extraFormat && !list.some((f) => f.key === extraFormat.key)) {
      list.push({ key: extraFormat.key, label: extraFormat.label, qty: 0, refId: null });
    }
    return list;
  }, [references, activeYear, mode, extraFormat]);

  // Pré-remplit prix d'achat / fournisseur en mode entrée depuis la référence.
  useEffect(() => {
    if (mode === 'entree' && selectedRef) {
      setPurchase(selectedRef.purchase_price_ht != null ? String(selectedRef.purchase_price_ht) : '');
      setSupplier(selectedRef.supplier_name || '');
    }
  }, [mode, selectedRef?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickYear = (y) => { const r = references.find((x) => x.year === y && x.quantity > 0) || references.find((x) => x.year === y); if (r) { setRefId(r.id); setExtraFormat(null); } };
  const pickFormat = (f) => { if (f.refId) setRefId(f.refId); else setExtraFormat({ key: f.key, label: f.label }); };

  const price = unitPrice(selectedRef, motif, quantity);
  const priceMasked = isPublic && motif === 'offert_perso';
  const stock = selectedRef?.quantity ?? 0;
  const afterStock = mode === 'sortie'
    ? Math.max(0, stock - (motif === 'verre' ? 0 : quantity))
    : stock + quantity;

  const invalidate = () => { invalidateStock(qc); };

  const label = cuvee ? `${cuvee.name} ${activeYear}` : 'Mouvement';

  const doSortie = async () => {
    if (!selectedRef) return;
    const res = await sortieExit({ reference_id: selectedRef.id, quantity, motif, table_note: note || null });
    invalidate();
    notify(`${quantity} × ${label} sortie · stock ${res.new_quantity}${res.offline ? ' · hors-ligne' : ''}`,
      () => sortieUndo(res.movement_id).then(invalidate));
    onDone?.(); onClose?.();
  };

  const doEntree = async () => {
    const fmt = selectedRef?.format || extraFormat?.key;
    if (!activeYear || !fmt) return;
    const payload = {
      cuvee_id: cuveeId, year: activeYear, format: fmt, quantity,
      purchase_price_ht: purchase ? parseFloat(String(purchase).replace(',', '.')) : null,
      supplier_name: supplier || null, location_code: selectedRef?.location_code || null,
      by_glass: byGlass, on_wine_list: onList,
    };
    const res = await scannerApi.entry(payload);
    invalidate();
    notify(`+${quantity} × ${label} en stock · stock ${res.new_quantity}`,
      () => scannerApi.undo(res.group_id).then(invalidate));
    onDone?.(); onClose?.();
  };

  const submit = mode === 'sortie' ? doSortie : doEntree;

  const body = (
    <div className="flex flex-col h-full" data-testid={`movement-panel-${mode}`}>
      {/* En-tête */}
      <div className="p-5 hair-b">
        <div className="flex items-start justify-between">
          <p className="kicker flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${mode === 'sortie' ? 'bg-[#7b2233]' : 'bg-gold'}`} /> {mode === 'sortie' ? 'Sortie' : 'Entrée en stock'}</p>
          {onClose && <button onClick={onClose} className="text-n500 hover:text-ink" data-testid="movement-close" aria-label="Fermer"><X className="w-5 h-5" /></button>}
        </div>
        <h2 className="font-title text-3xl leading-tight mt-1">{cuvee?.name}</h2>
        <p className="text-[13px] text-n600">{domain?.name}{cuvee?.appellation ? ` · ${cuvee.appellation}` : ''}</p>
      </div>

      <div className="flex-1 overflow-auto p-5 space-y-4">
        {/* Pastilles millésime */}
        <div>
          <p className="kicker mb-1.5">Millésime</p>
          <div className="flex flex-wrap gap-2">
            {years.map((y) => (
              <button key={y} onClick={() => pickYear(y)} data-testid={`mv-year-${y}`}
                className={`px-3 h-9 rounded-md border text-[13px] nums ${activeYear === y ? 'vd-selected border-gold' : 'border-hair hover:bg-surface'}`}>{y}</button>
            ))}
          </div>
        </div>

        {/* Pastilles format */}
        <div>
          <p className="kicker mb-1.5">Format</p>
          <div className="flex flex-wrap gap-2 items-center">
            {formatsForYear.map((f) => (
              <button key={f.key} onClick={() => pickFormat(f)} data-testid={`mv-format-${f.key}`}
                className={`px-3 h-9 rounded-md border text-[13px] flex items-center gap-1.5 ${(selectedRef?.format === f.key || extraFormat?.key === f.key) ? 'vd-selected border-gold' : f.qty === 0 ? 'border-dashed border-hair text-n500' : 'border-hair hover:bg-surface'}`}>
                {f.label} <span className="nums text-n500">{f.qty}</span>
              </button>
            ))}
            {mode === 'entree' && (
              <select value="" onChange={(e) => { const s = STD_FORMATS.find((x) => x.key === e.target.value); if (s) pickFormat({ key: s.key, label: s.label, refId: null }); }}
                className="vd-input h-9 w-32 text-[13px]" data-testid="mv-new-format">
                <option value="">+ Nouveau</option>
                {STD_FORMATS.filter((s) => !formatsForYear.some((f) => f.key === s.key)).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            )}
          </div>
        </div>

        {/* État emplacement / stock */}
        <div className="flex items-center justify-between text-[13px] hair-t hair-b py-2">
          <span className="flex items-center gap-2"><WineColorDot color={cuvee?.color} size={9} /> {selectedRef?.location_code || '—'}</span>
          <span className="text-n600 nums">Stock {stock} → {afterStock}</span>
        </div>

        {/* Quantité */}
        <div className="flex items-center justify-center py-1">
          <QuantityStepper value={quantity} min={1} max={mode === 'sortie' ? Math.max(1, stock) : undefined} onChange={setQuantity} shortcuts={false} size="lg" testId="mv-qty" />
        </div>
        {mode === 'entree' && (
          <div className="flex justify-center gap-2">
            {QUANTITY_SHORTCUTS.map((n) => (
              <button key={n} onClick={() => setQuantity(n)} className="vd-tag hover:bg-surface" data-testid={`mv-plus-${n}`}>+{n}</button>
            ))}
          </div>
        )}

        {mode === 'sortie' ? (
          <>
            <div><p className="kicker mb-1.5">Motif · facultatif</p><MotifPicker value={motif} onChange={setMotif} testId="mv-motif" /></div>
            <div className="flex items-baseline justify-between hair-t pt-3">
              <span className="text-[14px] text-n700">Prix {motif === 'verre' ? 'au verre' : motif}</span>
              <span className="text-2xl nums" data-testid="mv-price">{priceMasked ? <MaskedValue masked>{formatEuro(price ?? 0)}</MaskedValue> : (price == null ? '—' : formatEuro(price))}</span>
            </div>
            <input className="vd-input" placeholder="+ Table / note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="mv-note" />
          </>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="kicker block mb-1">Prix d'achat HT</span>
              <input className="vd-input" value={purchase} onChange={(e) => setPurchase(e.target.value)} placeholder="14,50" data-testid="mv-purchase" /></label>
            <label className="block"><span className="kicker block mb-1">Fournisseur</span>
              <input className="vd-input" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Direct domaine" data-testid="mv-supplier" /></label>
            <label className="flex items-center gap-2 text-[13px] cursor-pointer" data-testid="mv-by-glass">
              <input type="checkbox" className="accent-[#b68235] w-4 h-4" checked={byGlass} onChange={(e) => setByGlass(e.target.checked)} /> Proposer au verre</label>
            <label className="flex items-center gap-2 text-[13px] cursor-pointer" data-testid="mv-on-list">
              <input type="checkbox" className="accent-[#b68235] w-4 h-4" checked={onList} onChange={(e) => setOnList(e.target.checked)} /> Proposer à la carte</label>
          </div>
        )}
      </div>

      {/* Validation */}
      <div className="p-5 hair-t">
        <Button variant="primary" className="w-full" style={{ height: 60, fontSize: 16 }} onClick={submit} data-testid="mv-submit"
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}>
          {mode === 'sortie' ? `Sortir ${quantity} bouteille${quantity > 1 ? 's' : ''}` : `Entrer ${quantity} bouteille${quantity > 1 ? 's' : ''}`}
        </Button>
        <p className="text-center text-[12px] text-n500 mt-2">Entrée pour valider · annulable 10 s</p>
      </div>
    </div>
  );

  if (!asDrawer) return <div className="vd-card overflow-hidden">{body}</div>;

  // Tiroir glissant (feuille plein écran sur mobile).
  return (
    <div className="fixed inset-0 z-50" data-testid="movement-drawer">
      <div className="absolute inset-0 bg-ink/20 animate-fade-in" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[400px] bg-paper shadow-lg animate-fade-in">
        {body}
      </aside>
    </div>
  );
}
